import { type NestedGroupItem } from 'component-library';
import { isLocationVisible } from '../locationRow';
import { type LocationGroup, type LocationListItemsType } from '../types';

const COPY_SEPARATOR = '#';

/** The location a dropdown value stands for. */
export const toLocationValue = (value: string) => value.split(COPY_SEPARATOR)[0];

const unique = (values: string[]) => [...new Set(values)];

/**
 * A location can sit under more than one parent. DropdownV3 needs unique values, so every copy
 * after the first gets a numbered suffix.
 */
export const toDropdownItems = (
  items: LocationListItemsType[],
  hideInactiveLocations: boolean,
  copiesByValue = new Map<string, number>(),
): NestedGroupItem[] =>
  items.flatMap((item) => {
    if (!isLocationVisible(item, hideInactiveLocations)) {
      return [];
    }

    const copies = copiesByValue.get(item.value) ?? 0;

    copiesByValue.set(item.value, copies + 1);

    return [
      {
        label: item.label,
        selectedLabel: item.altLabel,
        value: copies === 0 ? item.value : `${item.value}${COPY_SEPARATOR}${copies}`,
        type: item.meta?.isUnselectable ? 'group' : 'value',
        children: toDropdownItems(item.children, hideInactiveLocations, copiesByValue),
      },
    ];
  });

const flatten = (items: NestedGroupItem[]): NestedGroupItem[] =>
  items.flatMap((item) => [item, ...flatten(item.children)]);

const getSelectableValues = (item: NestedGroupItem): string[] =>
  flatten([item])
    .filter((node) => node.type === 'value')
    .map((node) => node.value);

/** The dropdown values of each selectable location, one per copy. */
export const getDropdownValuesByLocation = (items: NestedGroupItem[]) => {
  const valuesByLocation = new Map<string, string[]>();

  items.flatMap(getSelectableValues).forEach((value) => {
    const location = toLocationValue(value);

    valuesByLocation.set(location, [...(valuesByLocation.get(location) ?? []), value]);
  });

  return valuesByLocation;
};

/** Maps a dropdown selection to locations. Unchecking one copy of a location unchecks them all. */
export const toLocationValues = (values: string[], previousValues: string[]) => {
  const valueSet = new Set(values);
  const uncheckedLocations = new Set(
    previousValues.filter((value) => !valueSet.has(value)).map(toLocationValue),
  );

  return unique(values.map(toLocationValue)).filter(
    (location) => !uncheckedLocations.has(location),
  );
};

type ShownSelectionOptions = {
  selectedValues: string[];
  items: NestedGroupItem[];
  locationItemsByKey: Record<string, LocationListItemsType | undefined>;
  includeBusinessAddress: boolean;
};

/**
 * The selection without the locations the tree hides, such as inactive ones. Business address
 * ids are not in the tree, so they stay. A selection the tree doesn't know at all also stays,
 * as it did in the old dropdown.
 */
export const getShownSelection = ({
  selectedValues,
  items,
  locationItemsByKey,
  includeBusinessAddress,
}: ShownSelectionOptions) => {
  const selectableLocations = getDropdownValuesByLocation(items);
  const shownLocations = new Set(flatten(items).map((item) => toLocationValue(item.value)));

  const isShown = (value: string) => {
    if (!locationItemsByKey[value]) {
      return includeBusinessAddress;
    }

    return includeBusinessAddress ? shownLocations.has(value) : selectableLocations.has(value);
  };

  const shownValues = selectedValues.filter(isShown);
  const onlyUnknownDropped = selectedValues.every(
    (value) => isShown(value) || !locationItemsByKey[value],
  );

  return shownValues.length === 0 && onlyUnknownDropped ? selectedValues : shownValues;
};

/** The selection without selectable parents whose children aren't all selected, as DropdownV3 keeps it. */
export const dropPartlySelectedParents = (items: NestedGroupItem[], selectedValues: string[]) => {
  const selectedSet = new Set(selectedValues);

  const visit = (item: NestedGroupItem) => {
    item.children.forEach(visit);

    const descendants = item.children.flatMap(getSelectableValues);

    if (item.type === 'value' && !descendants.every((value) => selectedSet.has(value))) {
      selectedSet.delete(item.value);
    }
  };

  items.forEach(visit);

  return selectedValues.filter((value) => selectedSet.has(value));
};

export const getInitialValues = (
  items: NestedGroupItem[],
  autoSelect: 'first' | 'all',
  multiSelect: boolean,
): string[] => {
  const candidates =
    autoSelect === 'all'
      ? items.flatMap(getSelectableValues)
      : (items.map(getSelectableValues).find((values) => values.length > 0) ?? []);
  const locations = unique(candidates.map(toLocationValue));

  return multiSelect ? locations : locations.slice(0, 1);
};

/**
 * One group per topmost fully selected item, as listed in the trigger, holding the selected
 * locations below it.
 */
export const getLocationGroups = (
  items: NestedGroupItem[],
  selectedValues: string[],
): LocationGroup[] => {
  const selectedSet = new Set(selectedValues);

  const isFullySelected = (item: NestedGroupItem) => {
    if (item.type === 'value') {
      return selectedSet.has(item.value);
    }

    const values = getSelectableValues(item);

    return values.length > 0 && values.every((value) => selectedSet.has(value));
  };

  const collect = (groupItems: NestedGroupItem[]): LocationGroup[] =>
    groupItems.flatMap((item) => {
      if (!isFullySelected(item)) {
        return collect(item.children);
      }

      return [
        {
          rootPartnerId: toLocationValue(item.value),
          allPartnerIds: unique(
            getSelectableValues(item)
              .filter((value) => selectedSet.has(value))
              .map(toLocationValue),
          ),
        },
      ];
    });

  const groups = collect(items);

  return groups.filter(
    (group, index) =>
      groups.findIndex(({ rootPartnerId }) => rootPartnerId === group.rootPartnerId) === index,
  );
};
